import { useCallback, useMemo, useState } from 'react';
import { getUserValue } from '../utils/reactQueryHooks';
import { QUICK_ACTION_RECIPES, normalizePresetOptions } from '../utils/quickActionUtils';

const STORAGE_PREFIX = 'canopy.quickActionPresets';

function presetStorageKey(userKey) {
  return `${STORAGE_PREFIX}.${userKey || 'anonymous'}`;
}

function createPresetId(name) {
  const slug = name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${slug || 'quick-action'}-${Date.now()}`;
}

function readPresets(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.warn('Unable to read quick action presets:', error);
    return [];
  }
}

function writePresets(key, presets) {
  localStorage.setItem(key, JSON.stringify(presets));
}

export default function useQuickActionPresets() {
  const userKey = getUserValue();
  const storageKey = useMemo(() => presetStorageKey(userKey), [userKey]);
  const [presets, setPresets] = useState(() => readPresets(storageKey));

  const persist = useCallback(
    (nextPresets) => {
      setPresets(nextPresets);
      writePresets(storageKey, nextPresets);
    },
    [storageKey]
  );

  const savePreset = useCallback(
    (preset) => {
      const now = new Date().toISOString();
      const normalized = {
        id: preset.id || createPresetId(preset.name),
        name: preset.name?.trim() || 'Quick action preset',
        actionMacs: [...new Set(preset.actionMacs || [])],
        recipe: preset.recipe || QUICK_ACTION_RECIPES.EXTEND_LIGHTS,
        options: normalizePresetOptions(
          preset.recipe || QUICK_ACTION_RECIPES.EXTEND_LIGHTS,
          preset.options || {}
        ),
        lastSnapshot: preset.lastSnapshot || {},
        createdAt: preset.createdAt || now,
        updatedAt: now,
      };

      const exists = presets.some((item) => item.id === normalized.id);
      const next = exists
        ? presets.map((item) => (item.id === normalized.id ? normalized : item))
        : [normalized, ...presets];

      persist(next);
      return normalized;
    },
    [persist, presets]
  );

  const deletePreset = useCallback(
    (presetId) => {
      persist(presets.filter((preset) => preset.id !== presetId));
    },
    [persist, presets]
  );

  const updatePresetSnapshot = useCallback(
    (presetId, snapshot) => {
      const next = presets.map((preset) =>
        preset.id === presetId
          ? {
              ...preset,
              lastSnapshot: snapshot || {},
              updatedAt: new Date().toISOString(),
            }
          : preset
      );
      persist(next);
    },
    [persist, presets]
  );

  const clearPresetSnapshot = useCallback(
    (presetId) => {
      updatePresetSnapshot(presetId, {});
    },
    [updatePresetSnapshot]
  );

  return {
    presets,
    savePreset,
    deletePreset,
    updatePresetSnapshot,
    clearPresetSnapshot,
  };
}
